export interface ILoadDataResponse {
    message: string;
    statusCode: number;
    statusText: string,
    responseText: string
}

export interface IResponseTextError {
    code: number;
    error: string;
}

export interface ISettings {
    [key: string]: string | number;

    systemId: string;
    systemName: string;
    systemOwner: string;
    systemCreatedAt: string;
    systemUpdatedAt: string;
    systemHost: string;
    systemPort: number;
    systemProtocol: string;
    systemLanguage: string;
    systemLocale: string;
    systemRegion: string;
}

export interface ILocales {
    [key: string]: string | string[] | number[];

    localeId: string;
    countries: string[];
    languages: string[];
    dateFormats: string[];
    timeFormats: string[];
    dateTimeFormats: string[];
    currencyCodes: string[];
    currencySymbols: string[];
    countryCodes: string[];
    countryNames: string[];
    phoneCountryCodes: string[];
    timeZones: string[]; // Time zone identifiers (e.g., America/New_York)
}

export interface IInstances {
    [key: string]: string[];

    protocols: string[];
    ports: string[];
    regions: string[];
}

export interface ICompany {
    [key: string]: string | boolean | ICompany[] | null;

    companyId: string;
    companyName: string;
    registrationNumber: string;
    registrationName: string;
    taxId: string;
    vatId: string;
    iban: string;
    address: string;
    city: string;
    country: string;
    zipCode: string;
    phone: string;
    email: string;
    website: string;
    logo: string;
    // System Information
    parentId: string;
    createdAt: string;
    updatedAt: string;
    isActive: boolean;
    locale: string;
    smtpProvider: string;
    smtpUsername: string;
    smtpPassword: string;
    smtpIdentity?: string;
    smtpRegion?: string;
    // optional fields for company hierarchy
    nodes?: ICompany[]; // For tree structure
}

export interface IUser {
    [key: string]: string | boolean | null | object;

    userId: string;
    firstName: string;
    lastName: string;
    gender: string;
    birthday: string | null;
    address: string;
    city: string;
    zipCode: string;
    country: string;
    nationality: string;
    email: string;
    emailVerified: boolean;
    phone: string;
    phoneVerified: boolean;
    password: string;
    newPassword?: string;
    repeatNewPassword?: string;
    company?: ICompany;
    // System Information
    companyId: string;
    createdAt: string;
    updatedAt: string;
    isActive: boolean;
    locale: string;
    theme: string;
}

export interface IPackage {
    [key: string]: string | boolean;

    packageId: string;
    packageKey: string;
    packageName: string;
    packageDescription: string;
    packageSubtitle: string;
    packageHref: string;
    packageIcon: string;
    // System Information
    createdAt: string;
    updatedAt: string;
    isActive: boolean;
    locale: string;
    parentId: string;
}

export interface ISession {
    [key: string]: string | boolean | object | null;

    sessionId: string;
    userId: string;
    companyId: string;
    userEmail: string;     // enriched: resolved from userId
    companyName: string;   // enriched: resolved from companyId
    ipAddress: string;
    userAgent: string;
    expiresAt: string; // ISO datetime string
    lastActivity: string; // ISO datetime string
    payload: object | null;
    createdAt: string; // ISO datetime string
    updatedAt: string; // ISO datetime string
    isActive: boolean;
    locale: string;
}

export const EU_VAT_PATTERNS: Record<string, RegExp> = {
    AT: /^U\d{8}$/,
    BE: /^[01]\d{9}$/,
    BG: /^\d{9,10}$/,
    CY: /^\d{8}[A-Z]$/,
    CZ: /^\d{8,10}$/,
    DE: /^\d{9}$/,
    DK: /^\d{8}$/,
    EE: /^\d{9}$/,
    EL: /^\d{9}$/,
    ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
    FI: /^\d{8}$/,
    FR: /^[A-Z0-9]{2}\d{9}$/,
    HR: /^\d{11}$/,
    HU: /^\d{8}$/,
    IE: /^(\d{7}[A-Z]{1,2}|\d[A-Z+*]\d{5}[A-Z])$/,
    IT: /^\d{11}$/,
    LT: /^(\d{9}|\d{12})$/,
    LU: /^\d{8}$/,
    LV: /^\d{11}$/,
    MT: /^\d{8}$/,
    NL: /^\d{9}B\d{2}$/,
    PL: /^\d{10}$/,
    PT: /^\d{9}$/,
    RO: /^\d{2,10}$/,
    SE: /^\d{12}$/,
    SI: /^\d{8}$/,
    SK: /^\d{10}$/,
    XI: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
};

export interface IVIESResponse {
    countryCode: string;
    vatNumber: string;
    requestDate?: string;
    valid: boolean
    requestIdentifier?: string; // "---"
    name?: string; // "---"
    address?: string; // "---"
    traderName?: string; // "---"
    traderStreet?: string; // "---"
    traderPostalCode?: string; // "---"
    traderCity?: string; // "---"
    traderCompanyType?: string; // "---"
    traderNameMatch?: string; // "NOT_PROCESSED"
    traderStreetMatch?: string; // "NOT_PROCESSED"
    traderPostalCodeMatch?: string; // "NOT_PROCESSED"
    traderCityMatch?: string; // "NOT_PROCESSED"
    traderCompanyTypeMatch?: string; // "NOT_PROCESSED"
    userError?: string; // client error formatter validation
}


export type EUCountryCode = keyof typeof EU_VAT_PATTERNS;

export interface IVATValidationResult {
    isValid: boolean;
    countryCode?: EUCountryCode;
    cleanVatNumber?: string;
    error?: string;
}
